// Plantillas de los recordatorios de cobranza. Funciones puras: se testean sin montar
// componentes.
//
// Billing es la única fuente del texto final: arma las variables, recorta el detalle de
// notas y formatea montos (POST templates/preview). Acá solo se preparan los pedidos, se
// ordenan los problemas que devuelve y se muestran contadores orientativos. Los mensajes de
// los problemas llegan en español desde billing y pueden traer tokens {{…}}: NUNCA se pasan
// por t() (la interpolación de i18next los reemplazaría).

import {
  CHANNEL_LABELS,
  REMINDER_CHANNELS,
  apiErrorCode,
  apiErrorStatus,
  collectionsErrorText,
  selectedChannels,
} from './collectionReminders';

/** Id lógico de la plantilla del sistema en las respuestas de billing. */
export const SYSTEM_TEMPLATE_ID = 'system';

/**
 * Avisos que describen los DATOS de la vista previa (no el texto de la plantilla): se
 * muestran junto a la vista previa en vez de debajo del campo.
 */
const PREVIEW_DATA_WARNING_CODES = [
  'pay_link_unavailable',
  'notes_truncated',
  'company_without_collectible_notes',
  'preview_notes_capped',
];

/** Variantes de ejemplo que billing devuelve junto a la vista previa principal. */
export const SAMPLE_VARIANT_LABELS = {
  single_note: 'Una sola nota',
  no_company_name: 'Comercio sin nombre',
  no_pay_link: 'Sin enlace de pago',
};

/** Plantillas activas de un canal, en el orden de billing (por nombre). */
export function templatesOfChannel(catalog, channel) {
  const items = Array.isArray(catalog?.items) ? catalog.items : [];
  return items.filter((item) => item?.channel === channel);
}

/** Plantilla activa por id, o `null`. */
export function findTemplate(catalog, templateId) {
  if (!templateId) return null;
  const items = Array.isArray(catalog?.items) ? catalog.items : [];
  return items.find((item) => item?.id === templateId) ?? null;
}

/** ¿El canal llegó al tope de plantillas activas? Sin límites conocidos no se bloquea. */
export function isChannelAtLimit(catalog, channel) {
  const max = Number(catalog?.limits?.maxActiveTemplatesPerChannel);
  if (!Number.isInteger(max) || max <= 0) return false;
  return templatesOfChannel(catalog, channel).length >= max;
}

/** Variables del catálogo que se pueden usar en el cuerpo de un canal. */
export function variablesForChannel(catalog, channel) {
  const variables = Array.isArray(catalog?.variables) ? catalog.variables : [];
  return variables.filter((v) => Array.isArray(v?.channels) && v.channels.includes(channel));
}

const DETAIL_TOKEN_PATTERN = /\{\{\s*detalle_notas\s*\}\}/g;

/**
 * Largo del cuerpo como lo mide billing para su tope: en WhatsApp no cuenta
 * `{{detalle_notas}}`, que se arma aparte dentro del límite del mensaje. Es solo el contador
 * del campo; billing valida igual.
 */
export function templateBodyLength(channel, body) {
  const text = String(body ?? '').replace(/\r\n?/g, '\n');
  return channel === 'whatsapp' ? text.replace(DETAIL_TOKEN_PATTERN, '').length : text.length;
}

/** Tope del cuerpo del canal según billing, o `null` si no se conoce. */
export function bodyMaxLength(limits, channel) {
  const max = Number(limits?.bodyMaxLength?.[channel]);
  return Number.isInteger(max) && max > 0 ? max : null;
}

// ── Borrador del editor ─────────────────────────────────────────────────────────────────

/** Borrador vacío (el asunto solo se usa en correo). */
export const emptyTemplateDraft = () => ({ name: '', subject: '', body: '' });

/** Borrador a partir de una plantilla (propia o del sistema). */
export const draftFromTemplate = (template) => ({
  name: template?.name ?? '',
  subject: template?.subject ?? '',
  body: template?.body ?? '',
});

/**
 * Nombre propuesto para la copia de una plantilla, recortado al tope de billing. El prefijo
 * ya viene traducido; el nombre es texto del operador y no pasa por t().
 */
export function copyTemplateName(prefix, name, maxLength) {
  const full = `${prefix} ${String(name ?? '').trim()}`.trim();
  return Number.isInteger(maxLength) && maxLength > 0 ? full.slice(0, maxLength).trim() : full;
}

/** ¿El borrador cambió respecto del inicial? (el asunto solo cuenta en correo). */
export function isTemplateDraftDirty(channel, draft, initial) {
  if (!initial) return false;
  if (draft.name !== initial.name || draft.body !== initial.body) return true;
  return channel === 'email' && draft.subject !== initial.subject;
}

// Como billing guarda los campos (cleanTemplate): nombre sin espacios en los bordes ni
// repetidos, asunto sin espacios en los bordes y el texto con saltos de línea \n.
const storedName = (name) => String(name ?? '').trim().replace(/\s+/g, ' ');
const storedSubject = (subject) => String(subject ?? '').trim();
const storedBody = (body) => String(body ?? '').replace(/\r\n?/g, '\n');

/**
 * ¿Dos borradores quedan guardados igual en billing? Sirve para saber si una edición cuya
 * respuesta se perdió ya quedó guardada (la plantilla releída tiene lo que se mandó).
 */
export function sameTemplateContent(channel, a, b) {
  if (storedName(a?.name) !== storedName(b?.name)) return false;
  if (storedBody(a?.body) !== storedBody(b?.body)) return false;
  return channel !== 'email' || storedSubject(a?.subject) === storedSubject(b?.subject);
}

/**
 * Body de POST templates. En WhatsApp no va asunto (billing lo rechaza); en correo va aunque
 * esté vacío, para que billing lo marque como obligatorio junto al campo.
 */
export function buildTemplateCreatePayload(channel, draft, idempotencyKey) {
  const payload = { channel, name: draft.name, body: draft.body };
  if (channel === 'email') payload.subject = draft.subject;
  if (idempotencyKey) payload.idempotencyKey = idempotencyKey;
  return payload;
}

/** Body de PUT templates/:id: el canal no cambia y `expectedVersion` detecta ediciones cruzadas. */
export function buildTemplateUpdatePayload(channel, draft, expectedVersion) {
  const payload = { name: draft.name, body: draft.body };
  if (channel === 'email') payload.subject = draft.subject;
  payload.expectedVersion = expectedVersion;
  return payload;
}

/**
 * Body de POST templates/preview. Con `source: 'company'` hace falta el comercio; con
 * 'sample' no se manda (billing lo ignoraría igual).
 */
export function buildTemplatePreviewPayload({ channel, subject, body, source, companyId }) {
  const payload = { channel };
  if (channel === 'email') payload.subject = subject ?? '';
  payload.body = body ?? '';
  payload.source = source;
  if (source === 'company') payload.companyId = companyId;
  return payload;
}

// ── Problemas de billing (errores y avisos) ─────────────────────────────────────────────

const asList = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

/**
 * Problemas de un campo. Los avisos de los datos de la vista previa quedan afuera: se
 * muestran junto a la vista previa.
 */
export function problemsOfField(problems, field) {
  return asList(problems).filter(
    (p) => p.field === field && !PREVIEW_DATA_WARNING_CODES.includes(p.code),
  );
}

/** Avisos sobre los datos de la vista previa (enlace sin configurar, notas recortadas…). */
export function previewDataWarnings(warnings) {
  return asList(warnings).filter((p) => PREVIEW_DATA_WARNING_CODES.includes(p.code));
}

/** `{ errors, warnings }` del 400 template_invalid de billing, o `null` si es otro error. */
export function templateInvalidProblems(err) {
  if (apiErrorCode(err) !== 'template_invalid') return null;
  const data = err?.response?.data ?? {};
  return { errors: asList(data.errors), warnings: asList(data.warnings) };
}

/** Pasos (días) donde se usa una plantilla, ordenados. */
export function usedInSteps(usedIn) {
  return asList(usedIn?.steps)
    .map(Number)
    .filter((n) => Number.isInteger(n))
    .sort((a, b) => a - b);
}

const TEMPLATE_ERROR_LABELS = {
  template_invalid: 'La plantilla tiene errores. Revisalos junto a cada campo.',
  template_name_taken: 'Ya existe una plantilla con ese nombre para este canal.',
  template_version_conflict:
    'Otra persona modificó esta plantilla mientras la editabas. Cargá la versión actual antes de guardar.',
  template_not_found: 'La plantilla ya no existe o fue archivada.',
  template_in_use: 'La plantilla está en uso en la configuración de cobranza.',
  invalid_template_reference:
    'Alguna de las plantillas elegidas ya no está disponible: se archivó o es de otro canal. Recargá la página y volvé a elegirla.',
  company_not_found: 'Este comercio no tiene notas de cobro: elegí otro o usá los datos de ejemplo.',
  idempotency_key_reused:
    'Un intento anterior de guardar ya quedó registrado con otros datos. Revisá la lista de plantillas: si volvés a guardar, se crea una plantilla nueva.',
};

/**
 * Texto para mostrar de un error de la API de plantillas. Los códigos conocidos se traducen
 * (el tope de plantillas con su número); el resto sigue el criterio general de cobranza.
 *
 * @param {unknown} err error de axios
 * @param {(key: string, opts?: object) => string} t
 * @param {string} fallback clave i18n
 */
export function templateErrorText(err, t, fallback) {
  const code = apiErrorCode(err);
  if (code === 'template_limit_reached') {
    const max = Number(err?.response?.data?.max);
    return Number.isInteger(max)
      ? t('Llegaste al máximo de {{max}} plantillas activas para este canal. Archivá alguna para crear otra.', { max })
      : t('Llegaste al máximo de plantillas activas para este canal. Archivá alguna para crear otra.');
  }
  if (code && TEMPLATE_ERROR_LABELS[code]) return t(TEMPLATE_ERROR_LABELS[code]);
  if (apiErrorStatus(err) === 429) {
    return t('Hiciste muchos pedidos seguidos. Esperá unos segundos y volvé a intentar.');
  }
  return collectionsErrorText(err, t, fallback);
}

/**
 * Texto de un error al guardar o previsualizar el BORRADOR del editor. Un 400 sin `code` es
 * el validador de transporte de billing (topes duros del pedido, muy por encima de los del
 * producto, o un formato que no es texto): su mensaje crudo llega en inglés y sin campo, así
 * que se explica en términos del editor.
 */
export function templateDraftErrorText(err, t, fallback) {
  if (apiErrorStatus(err) === 400 && !apiErrorCode(err)) {
    return t('El texto excede el tamaño permitido o tiene un formato no válido.');
  }
  return templateErrorText(err, t, fallback);
}

/**
 * Texto del límite de vistas previas por usuario de billing (429 `preview_rate_limited`
 * con `retryAfterSeconds`), o `null` si el error es otro. No hay reintento automático: el
 * operador vuelve a pedirla con "Actualizar" (o al seguir editando).
 */
function previewRateLimitText(err, t) {
  if (apiErrorStatus(err) !== 429) return null;
  const seconds = Number(err?.response?.data?.retryAfterSeconds);
  if (apiErrorCode(err) === 'preview_rate_limited' && Number.isFinite(seconds) && seconds > 0) {
    return t('Hiciste muchas vistas previas seguidas. Esperá {{n}} s y tocá "Actualizar".', {
      n: Math.ceil(seconds),
    });
  }
  return t('Hiciste muchos pedidos de vista previa seguidos. Esperá unos segundos y tocá "Actualizar".');
}

/** Texto de un error de la vista previa del editor de plantillas. */
export function templatePreviewErrorText(err, t) {
  return previewRateLimitText(err, t) ?? templateDraftErrorText(err, t, 'No se pudo armar la vista previa.');
}

// ── Envíos (enviar ahora / programar) y su historial ───────────────────────────────────

const templateIdOrNull = (value) => (typeof value === 'string' && value ? value : null);

/**
 * Plantilla por defecto de cada canal según la configuración (`null` = la del sistema), o
 * `null` si los settings no traen `defaultTemplateIds` (billing sin plantillas, que responde
 * 400 a un `templateIds`): entonces los formularios de envío no ofrecen elegir plantilla.
 */
export function operatorDefaultTemplateIds(settings) {
  const defaults = settings?.defaultTemplateIds;
  if (defaults === null || typeof defaults !== 'object') return null;
  return {
    email: templateIdOrNull(defaults.email),
    whatsapp: templateIdOrNull(defaults.whatsapp),
  };
}

/**
 * `templateIds` de un envío: la plantilla elegida (id, o `null` = la del sistema) SOLO de
 * los canales que se mandan. Se manda explícita aunque sea la por defecto: así la elegida
 * no depende de un cambio de la plantilla por defecto entre abrir y confirmar. Que además
 * sea la VERSIÓN que se vio en la vista previa lo cuida `expectedTemplateVersions`.
 * `undefined` si no hay elección (billing sin plantillas) o ningún canal.
 */
export function requestedTemplateIds(channels, templateIds) {
  if (!templateIds) return undefined;
  const selected = selectedChannels(channels);
  if (selected.length === 0) return undefined;
  return Object.fromEntries(selected.map((channel) => [channel, templateIdOrNull(templateIds[channel])]));
}

/**
 * `expectedTemplateVersions` de un envío: por canal que se manda, la versión de la
 * plantilla que el operador vio en la vista previa (`item.template.version`; `null` = la del
 * sistema). Billing la compara con la que congelaría y responde 409 `template_changed` si
 * otra persona editó la plantilla en el medio: nunca sale un texto que no se vio.
 *
 * Solo va un canal cuya vista previa muestra ESA plantilla (el mismo id que se manda): la
 * de otra plantilla (todavía cargando o de un pedido que falló) no prueba qué versión se
 * vio. `undefined` si no queda ningún canal.
 */
export function expectedTemplateVersions(channels, templateIds, previewItems) {
  const requested = requestedTemplateIds(channels, templateIds);
  if (!requested) return undefined;
  const items = asList(previewItems);
  const expected = {};
  for (const [channel, templateId] of Object.entries(requested)) {
    const shownId = templateId ?? SYSTEM_TEMPLATE_ID;
    const item = items.find((i) => i.channel === channel && i.template?.id === shownId);
    if (!item) continue;
    if (shownId === SYSTEM_TEMPLATE_ID) expected[channel] = null;
    else if (Number.isInteger(item.template.version)) expected[channel] = item.template.version;
  }
  return Object.keys(expected).length > 0 ? expected : undefined;
}

/**
 * Body de POST reminders/preview (sin `companyId` ni `limit`, billing usa su página por
 * defecto). Con `sendAt` (Programar) billing omite, como al programar, las notas que ya
 * esperan en otro programado a menos de 24 h por el mismo canal (`skipped` con reason
 * 'already_scheduled').
 */
export function buildReminderPreviewPayload({ noteIds, channels, templateIds, companyId, limit, sendAt }) {
  const payload = {
    noteIds: Array.isArray(noteIds) ? noteIds : [],
    channels: { email: channels?.email === true, whatsapp: channels?.whatsapp === true },
  };
  const requested = requestedTemplateIds(channels, templateIds);
  if (requested) payload.templateIds = requested;
  if (companyId) payload.companyId = companyId;
  if (limit) payload.limit = limit;
  if (sendAt) payload.sendAt = { date: sendAt.date, hour: sendAt.hour };
  return payload;
}

/**
 * Notas que la vista previa dejó afuera (`skipped`), contadas sin repetir: las que ya
 * esperan en otro programado cercano por el mismo canal (`already_scheduled`, solo con
 * `sendAt`) y el resto (no cobrables o inexistentes).
 */
export function previewSkippedCounts(skipped) {
  const alreadyScheduled = new Set();
  const other = new Set();
  for (const entry of asList(skipped)) {
    (entry.reason === 'already_scheduled' ? alreadyScheduled : other).add(entry.noteId);
  }
  return { alreadyScheduled: alreadyScheduled.size, other: other.size };
}

/**
 * Canales cuya plantilla rechazó billing: 400 `invalid_template_reference` con `fields`
 * 'templateIds.<canal>' (archivada, de otro canal o de otro operador: billing no distingue).
 */
export function invalidTemplateChannels(err) {
  if (apiErrorCode(err) !== 'invalid_template_reference') return [];
  const fields = asList(err?.response?.data?.fields);
  return REMINDER_CHANNELS.filter((channel) => fields.includes(`templateIds.${channel}`));
}

/** ¿Billing rechazó el envío porque la plantilla cambió desde la vista previa? (409, sin crear nada) */
export const isTemplateChanged = (err) => apiErrorCode(err) === 'template_changed';

/** Texto de un error de "Enviar"/"Programar": la plantilla rechazada se explica aparte. */
export function reminderSendErrorText(err, t, fallback) {
  if (apiErrorCode(err) === 'invalid_template_reference') {
    return t('La plantilla elegida ya no está disponible: se archivó o es de otro canal. Elegí otra y volvé a intentar.');
  }
  if (isTemplateChanged(err)) {
    const channel = err?.response?.data?.channel;
    return CHANNEL_LABELS[channel]
      ? t('La plantilla de {{canal}} cambió después de la vista previa. Revisá el mensaje actualizado y volvé a confirmar.', {
          canal: t(CHANNEL_LABELS[channel]),
        })
      : t('Una plantilla cambió después de la vista previa. Revisá el mensaje actualizado y volvé a confirmar.');
  }
  return collectionsErrorText(err, t, fallback);
}

/** Texto de un error de la vista previa de un envío. */
export function reminderPreviewErrorText(err, t) {
  const rateLimited = previewRateLimitText(err, t);
  if (rateLimited) return rateLimited;
  if (apiErrorCode(err) === 'invalid_template_reference') {
    return t('La plantilla elegida ya no está disponible: elegí otra para ver el mensaje.');
  }
  return collectionsErrorText(err, t, 'No se pudo armar la vista previa.');
}

/**
 * Nombre para mostrar de la plantilla de un mensaje (`{ id, name }` de billing): la del
 * sistema se traduce; la del operador es su texto y va tal cual, sin t().
 */
export function messageTemplateName(template, t) {
  if (!template || template.id === SYSTEM_TEMPLATE_ID) return t('Plantilla del sistema');
  return template.name || '—';
}

/**
 * Plantillas que pedían las filas de un mensaje agrupado cuando eran distintas de la que se
 * usó; vacío si el mensaje no mezcló plantillas.
 */
export const requestedTemplatesOf = (message) => asList(message?.requestedTemplates);
